import { useCallback, useMemo } from 'react';
import { orderBy } from 'lodash-es';
import { ResourceWarning } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks/useResourceWarnings/types';
import { createClinicalLeadWarningHandler } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks/useResourceWarnings/handlers';
import { getClinicalLeadColumns } from './config';
import { ClinicalLeadRowData, renderClinicalLeadTableCell } from './renderClinicalLeadTableCell';
import { searchCandidates } from '../ClinicalSpecialistsTable/utils';
import { Case, CaseUserGroup } from '@/api/apis/case';
import { useClinicalLeadsForAssignment } from '@/queries/resource/resource.query';
import { CandidatesTable } from '../components/CandidatesTable/CandidatesTable';

export type ClinicalLeadsTableProps = {
  onAssign: ({ id }: { id: string }) => void;
  toggleWarning: (warning: ResourceWarning) => void;
  idsToAssign?: string[];
  searchTerm: string;
  caseInfo: Case;
};

export const ClinicalLeadsTable = ({
  onAssign,
  idsToAssign = [],
  searchTerm,
  caseInfo,
  toggleWarning,
}: ClinicalLeadsTableProps) => {
  const { assignedEmployees, organType } = caseInfo;
  const clinicalLeads = assignedEmployees[CaseUserGroup.ClinicalLeads] || [];
  const assignedEmployeeIds = clinicalLeads.map((e) => e.employeeId).filter(Boolean) as string[];
  const primaryLeadId = clinicalLeads.find((e) => e.isPrimary)?.employeeId;

  const { data, isLoading } = useClinicalLeadsForAssignment(assignedEmployeeIds, organType);

  const items = useMemo(() => {
    const mappedLeads = (data ?? []).map((lead) => ({
      ...lead,
      isSelected: idsToAssign.includes(lead.employeeId),
      isPrimary: lead.employeeId === primaryLeadId,
    }));

    return orderBy(mappedLeads, 'isPrimary', 'desc');
  }, [data, idsToAssign, primaryLeadId]);

  const searchResults = useMemo(() => searchCandidates(searchTerm, items), [searchTerm, items]);

  const onCLAssign = useCallback(
    (candidate: ClinicalLeadRowData) => {
      const updateClinicalLeadWarning = createClinicalLeadWarningHandler(toggleWarning);
      if (!candidate.isAssigned) updateClinicalLeadWarning(candidate);
      onAssign({ id: candidate.employeeId });
    },
    [onAssign, toggleWarning],
  );

  const renderCell = useMemo(() => renderClinicalLeadTableCell(onCLAssign), [onCLAssign]);

  return (
    <CandidatesTable
      label="Clinical leads for assignment"
      columns={getClinicalLeadColumns()}
      renderCell={renderCell}
      data={searchResults}
      isLoading={isLoading}
      isFetching={isLoading}
    />
  );
};
